/**
 * Self-schedule calendar — multi-slot picker.
 * User selects days via checkboxes, system generates available slots.
 * Users can add/remove/drag individual slots.
 * Uses the Calendar component from @kula-ai/components with checkboxes enabled.
 */
import { useCallback, useMemo } from 'react';
import {
  Calendar,
  type CalendarProps,
  type SchedulerState,
} from '@kula-ai/components';

interface SelfScheduleCalendarProps {
  duration: number;
  timezone?: string;
  interviewerIds?: string[];
  meetingRoomIds?: string[];
  onSlotsChange?: (_slots: Array<{ start: string; end: string }>) => void;
  /** Initial slots from edit mode */
  initialSlots?: Array<{
    date: string;
    timeRanges: Array<{ startTime: string; endTime: string }>;
    enabled: boolean;
  }>;
  goToDate?: Date | string;
}

/**
 * Extracts all active slots from the scheduler state for payload building.
 */
function extractSlotsFromState(
  state: SchedulerState
): Array<{ start: string; end: string }> {
  const slots: Array<{ start: string; end: string }> = [];
  for (const weekKey of Object.keys(state)) {
    const week = state[weekKey];
    for (const dayKey of Object.keys(week.days)) {
      const day = week.days[dayKey];
      const active = [...day.slotsGenerated, ...day.customSlots].filter(
        (s) => !day.removedSlots.includes(s.id)
      );
      for (const slot of active) {
        slots.push({ start: slot.start, end: slot.end });
      }
    }
  }
  return slots;
}

export const SelfScheduleCalendar = ({
  duration,
  timezone,
  onSlotsChange,
  initialSlots,
  goToDate,
}: SelfScheduleCalendarProps) => {
  const bookedSlot = useMemo(() => ({ duration }), [duration]);

  const handleScheduleChange: CalendarProps['onScheduleChange'] = useCallback(
    (data) => {
      const slots = extractSlotsFromState(data.schedulerState);
      onSlotsChange?.(slots);
    },
    [onSlotsChange]
  );

  return (
    <Calendar
      bookedSlot={bookedSlot}
      onScheduleChange={handleScheduleChange}
      timezone={timezone}
      goToDate={goToDate}
      initialSlots={initialSlots}
      includeCheckboxForDay
      showTimezoneSelect
      disabledPastDays
      disablePastNavigation
      showNowIndicator
      height={600}
      maxWidth="100%"
      slotGridInterval="00:15:00"
      readOnly={false}
    />
  );
};
